export const isBoolean = (value: unknown): value is boolean => typeof value === "boolean";

export const isString = (value: unknown): value is string => typeof value === "string";

export const isNumber = (value: unknown): value is number => typeof value === "number";

export const isNullable = (value: unknown): value is null | undefined => value === null || value === undefined;

export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type RequiredBy<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

export type Nullable<T> = T | undefined | null;

export type NonNullableBy<T, K extends keyof T> = Omit<T, K> & {
  [P in K]-?: NonNullable<T[P]>;
};

export type NullableValues<T> = {
  [P in keyof T]?: Nullable<T[P]>;
};

export type ObjectByValuesOfType<T, V> = {
  [P in keyof T as T[P] extends V ? P : never]: T[P];
};

export type ArrayElement<A> = A extends readonly (infer T)[] ? T : never;

export type NullableArrayElement<T extends unknown[]> = Nullable<ArrayElement<T>>[];

export type RequiredTupleOf<U extends string, R extends unknown[] = []> = {
  [S in U]: Exclude<U, S> extends never ? [...R, S] : RequiredTupleOf<Exclude<U, S>, [...R, S]>;
}[U];

/**
 * Given a generic string type, returns that type but ensures that a string in general is compatible
 * with it. We use this construct to ensure that IDEs can provide better autocompletion for string
 * types. This is, for example, needed for main config fields, where we want to ensure that the user
 * can provide a custom string, but also a string that is compatible with the type.
 *
 * @example
 *
 * ```ts
 * type AirlineCode = CompatibleString<'iata_code'>;
 * const airlineCode: AirlineCode = 'iata_code'; // valid and will be autocompleted
 * const airlineCode: AirlineCode = airline.iata_code; // valid - can use dynamic value
 * ```
 */
export type CompatibleString<T extends string> = T | (string & {});

export type ValueOf<T> = T[keyof T];
